import { useState } from "react";
import { CalcLayout, NumField, Stat, GrowthChart } from "./shared";
import { sipSeries, lumpsumSeries, compoundSeries } from "../../lib/calc";
import { inr } from "../../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export function SipCalculator() {
  const [m, setM] = useState(10000);
  const [r, setR] = useState(12);
  const [y, setY] = useState(15);
  const [step, setStep] = useState(0);
  const res = sipSeries(m, r, y, step);
  return (
    <CalcLayout title="SIP Calculator" description="Estimate the future value of a monthly Systematic Investment Plan."
      inputs={<>
        <NumField id="sip-amount" label="Monthly investment" prefix="₹" value={m} onChange={setM} min={500} max={500000} step={500} />
        <NumField id="sip-rate" label="Expected return (p.a.)" suffix="%" value={r} onChange={setR} min={1} max={30} step={0.5} />
        <NumField id="sip-years" label="Time period" suffix="yrs" value={y} onChange={setY} min={1} max={40} />
        <NumField id="sip-stepup" label="Annual step-up" suffix="%" value={step} onChange={setStep} min={0} max={25} />
      </>}
      results={<>
        <Stat label="Total invested" value={inr(res.invested)} testid="sip-result-invested" />
        <Stat label="Est. gains" value={inr(res.gains)} testid="sip-result-gains" />
        <Stat label="Projected value" value={inr(res.value)} testid="sip-result-value" accent />
      </>}
      chart={<GrowthChart data={res.rows} />} />
  );
}

export function LumpsumCalculator() {
  const [p, setP] = useState(500000);
  const [r, setR] = useState(11);
  const [y, setY] = useState(10);
  const [er, setEr] = useState(1);
  const res = lumpsumSeries(p, r, y, er);
  return (
    <CalcLayout title="Mutual Fund Returns Calculator" description="Project a one-time (lumpsum) mutual fund investment, net of the scheme's expense ratio."
      inputs={<>
        <NumField id="mf-amount" label="Investment amount" prefix="₹" value={p} onChange={setP} min={1000} max={50000000} step={1000} />
        <NumField id="mf-rate" label="Expected gross return (p.a.)" suffix="%" value={r} onChange={setR} min={1} max={30} step={0.5} />
        <NumField id="mf-expense" label="Expense ratio" suffix="%" value={er} onChange={setEr} min={0} max={3} step={0.05} />
        <NumField id="mf-years" label="Holding period" suffix="yrs" value={y} onChange={setY} min={1} max={40} />
      </>}
      results={<>
        <Stat label="Net return used" value={`${res.netRate.toFixed(2)}%`} testid="mf-result-netrate" />
        <Stat label="Est. gains" value={inr(res.gains)} testid="mf-result-gains" />
        <Stat label="Projected value" value={inr(res.value)} testid="mf-result-value" accent />
      </>}
      chart={<GrowthChart data={res.rows} aLabel="Amount invested" />} />
  );
}

const FREQ = { Yearly: 1, "Half-yearly": 2, Quarterly: 4, Monthly: 12 };

export function CompoundCalculator() {
  const [p, setP] = useState(100000);
  const [r, setR] = useState(8);
  const [y, setY] = useState(10);
  const [f, setF] = useState("Quarterly");
  const res = compoundSeries(p, r, y, FREQ[f]);
  return (
    <CalcLayout title="Compound Interest Calculator" description="See how compounding frequency and time grow a principal amount. Compared with simple interest."
      inputs={<>
        <NumField id="ci-principal" label="Principal" prefix="₹" value={p} onChange={setP} min={1000} max={50000000} step={1000} />
        <NumField id="ci-rate" label="Annual interest rate" suffix="%" value={r} onChange={setR} min={1} max={25} step={0.25} />
        <NumField id="ci-years" label="Time period" suffix="yrs" value={y} onChange={setY} min={1} max={50} />
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-600">Compounding frequency</span>
          <Select value={f} onValueChange={setF}>
            <SelectTrigger className="w-40" data-testid="ci-frequency-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.keys(FREQ).map((k) => <SelectItem key={k} value={k} data-testid={`ci-frequency-${k.toLowerCase()}`}>{k}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </>}
      results={<>
        <Stat label="Effective annual rate" value={`${res.effective.toFixed(2)}%`} testid="ci-result-effective" />
        <Stat label="Total interest" value={inr(res.interest)} testid="ci-result-interest" />
        <Stat label="Maturity value" value={inr(res.value)} testid="ci-result-value" accent />
      </>}
      chart={<GrowthChart data={res.rows} aLabel="Simple interest value" bLabel="Compound value" />}
      note="Illustrative calculation only. Actual interest depends on the product terms, taxes and applicable rates. Not a guarantee of returns." />
  );
}
